import {
  ActivityFileTypeEnum,
  ActivityInterface,
  ActivityMediaInterface,
  ActivityTimeslot,
  ActivityTypeEnum,
  ActivityVisibilityEnum,
  FileTypeEnum,
  LocationInterface,
  MediaInterface,
  MetaInterface,
  OfferInterface,
  OrganizationProviderEnum,
  PriceInterface,
  ProficiencyLevelEnum,
  RangeInterface,
} from '@sport-activities/sdk';
import { Duration, formatDuration } from 'date-fns';

import { appConfig } from '~/app.config';
import { parseISO8601Duration } from '~/core/helpers/date/parse-iso-duration/parse-iso-duration.helper';
import { ActivityMediaResolutionEnum } from '~/organizer/shared/enums/media-resolution.enum';
import {
  getMediaUrlByIdentifier,
  getMediaUrlByType,
} from '~/organizer/shared/helpers/media/media.helper';

export class Activity implements ActivityInterface {
  public accountabilityReference: string;
  public activityUuid: string;
  public bookableUntilTheEnd: boolean | null;
  public organizerId: string;
  public dataSourceIdentifier: string;
  public dataSourceVersion: number;
  public description: string | null;
  public duration: string | null;
  public ecological: boolean | null;
  public external_id: string | null;
  public generateQrcode: boolean | null;
  public highestPrice: PriceInterface | null;
  public image: MediaInterface | null;
  public indicativeMaximumAttendeeCapacity: number | null;
  public indicativeMinimumAttendeeCapacity: number | null;
  public indicativeOffers: OfferInterface[] | null;
  public isBms?: boolean;
  public liveUrl: string | null;
  public location: LocationInterface;
  public medias: ActivityMediaInterface[] | null;
  public meta: MetaInterface | null;
  public name: string | null;
  public noteForAttendees: string | null;
  public organizationIdentifier: string;
  public organizationProvider?: OrganizationProviderEnum;
  public proficiencyLevel: ProficiencyLevelEnum[] | null;
  public providedEquipment: string[] | null;
  public refundTimeLimit: string | null;
  public requiredEquipment: string[] | null;
  public sku: number | string;
  public sports: number[] | null;
  public startingPrice: PriceInterface | null;
  public status: string | null;
  public tenant: string | null;
  public timeslots?: ActivityTimeslot[] | null;
  public type: ActivityTypeEnum | null;
  public typicalAgeRange: RangeInterface;
  public validUntil: string | null;
  public visibility: ActivityVisibilityEnum | null;

  constructor(init: Partial<Activity>) {
    Object.assign(this, init);
  }

  public get ageMax(): number {
    return this.typicalAgeRange.max;
  }

  /**
   * ACCESSORS
   */

  public get ageMin(): number {
    return this.typicalAgeRange.min;
  }

  public get bookingAttachments(): ActivityMediaInterface[] {
    return (this.medias || []).filter((media) =>
      media.types.includes('BOOKING_MAIL_ATTACHMENT' as FileTypeEnum)
    );
  }

  public get fullAddress(): string {
    let fullAddress = '';

    if (this.location?.address?.streetAddress) {
      fullAddress += this.location.address.streetAddress + ' ';
    }

    if (this.location?.address?.postalCode) {
      fullAddress += this.location.address.postalCode + ' ';
    }

    if (this.location?.address?.addressLocality) {
      fullAddress += this.location.address.addressLocality;
    }

    if (this.location?.address?.addressCountry) {
      if (fullAddress !== '') {
        fullAddress += ', ';
      }

      fullAddress += this.location.address.addressCountry;
    }

    return fullAddress.trim();
  }

  public get isCourse(): boolean {
    return this.type === ('MULTI_DAY_COURSE' as ActivityTypeEnum);
  }

  public get isEvent(): boolean {
    return this.type === ('EVENT' as ActivityTypeEnum);
  }

  public get isLive(): boolean {
    return this.type === ('LIVE' as ActivityTypeEnum);
  }

  public get isoDuration(): Duration | null {
    if (!this.duration) {
      return null;
    }

    return parseISO8601Duration(this.duration).duration;
  }

  public get maxPriceOffer(): OfferInterface {
    return this.indicativeOffers?.slice().sort((a, b) => b.price - a.price)[0];
  }

  public get minPriceOffer(): OfferInterface {
    return this.indicativeOffers?.slice().sort((a, b) => a.price - b.price)[0];
  }

  /**
   * get picture with fallback for sports image
   */
  public get picture(): string {
    if (this.image) {
      return this.image.url;
    }

    return appConfig.defaultImage;
  }

  public formattedDuration(locale?: Locale): string | null {
    if (!this.isoDuration) {
      return null;
    }

    const formatConfig = { locale };
    let duration = this.isoDuration;

    if (this.isCourse || this.isEvent) {
      duration = { days: duration.days + 1 }; // a 2 days course: 1d10h30m
    }

    return formatDuration(duration, formatConfig);
  }

  public getMediaUrlByIdentifier(
    identifier: string,
    mediaResolution: ActivityMediaResolutionEnum
  ): string {
    return getMediaUrlByIdentifier(this.medias, identifier, mediaResolution);
  }

  public getMediaUrlByType(
    mediaType: ActivityFileTypeEnum,
    mediaResolution: ActivityMediaResolutionEnum
  ): string {
    return getMediaUrlByType(this.medias, mediaType, mediaResolution);
  }
}
